/**
 * `<TaskTimeline/>` — the per-task timeline-of-updates engine (the headline of
 * the 2026-05-25 Claude-design bundle).
 *
 * A vertical, **newest-first**, day-grouped rail of everything that has
 * happened to a task: created · renamed · re-described · re-parented ·
 * re-ordered · assigned / unassigned · state changed · dependency linked /
 * unlinked · note added / removed · archived. Each entry is a calm factual
 * phrase (`describeEvent`) on a coloured rail dot (`eventIcon`), with the actor
 * shown quietly as provenance.
 *
 * One engine, two hosts: the compact drawer History tab (`compact`) and the
 * full-panel timeline. It also carries the founder's "This task & sub-tasks"
 * behaviour: when `showNodeAttribution` is on (subtree scope), an event that
 * belongs to a descendant is tagged "on «sub-task»" so a mixed stream stays
 * legible — about the WORK, never a person.
 *
 * For a long history, `collapsibleDays` folds older days behind a quiet pellet
 * strip (the design's A3), keeping recent days one glance away.
 *
 * Brand firewall (§A9): provenance, never a scorecard. Nothing here aggregates
 * or ranks a person; every count is of events on the work, never about anyone.
 */

import { useState } from "react";

import type { NodeHistoryItem } from "../api/useNodeHistory";
import type { TaskMember } from "../members/useTaskTeamMembers";
import { useMapTheme } from "../theme/MapThemeContext";
import type { MapTheme } from "../theme/tokens";
import { PALETTE } from "../tree/palette";
import { TimelineEntry } from "./TimelineEntry";
import { type DescribeLookups, describeEvent } from "./describeEvent";
import { EventIcon, eventVisual } from "./eventIcon";
import { type DayGroup, groupEventsByDay } from "./groupEventsByDay";

/** Above this many events, callers switch `collapsibleDays` on (the A3 layout). */
export const LONG_HISTORY_THRESHOLD = 40;

/** How many of the most-recent days stay open by default when collapsing. */
const OPEN_DAYS_WHEN_COLLAPSED = 2;

export interface TaskTimelineProps {
  /** The node's (or subtree's) events, any order — grouped newest-first here. */
  readonly events: NodeHistoryItem[];
  /** Team roster, to resolve actor + referenced-member names. */
  readonly members: TaskMember[];
  /** node id → title, for "created under …", dependency names, and subtree tags. */
  readonly nodeTitleById?: Map<string, string> | undefined;
  /** The focal node — its own events are NOT tagged with an attribution. */
  readonly focalNodeId: string;
  /** Subtree scope: tag any event whose node ≠ the focal node with "on «…»". */
  readonly showNodeAttribution?: boolean;
  /** Tighter layout for the 400px drawer. */
  readonly compact?: boolean;
  /** Fold older days behind a pellet strip (for long histories). */
  readonly collapsibleDays?: boolean;
  /** Team IANA zone for day bucketing + timestamps. */
  readonly tz?: string | undefined;
  /** Injected "now" for deterministic grouping in tests. */
  readonly nowISO?: string;
}

export function TaskTimeline({
  events,
  members,
  nodeTitleById,
  focalNodeId,
  showNodeAttribution = false,
  compact = false,
  collapsibleDays = false,
  tz,
  nowISO,
}: TaskTimelineProps) {
  const theme = useMapTheme();
  const byMembership = new Map(members.map((m) => [m.membershipId, m]));
  const lookups: DescribeLookups = {
    memberName: (id) => (id ? (byMembership.get(id)?.name ?? null) : null),
    nodeTitle: (id) => (id ? (nodeTitleById?.get(id) ?? null) : null),
  };

  const groups = groupEventsByDay(events, {
    tz,
    ...(nowISO ? { now: new Date(nowISO) } : {}),
  });

  // Collapsible days: track only the days the user has explicitly toggled; the
  // rest fall back to the default (most-recent N open). Keyed by the stable day
  // key so it survives a re-group (e.g. a scope switch keeps your folds).
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const isOpen = (key: string, index: number): boolean => {
    if (key in toggled) return toggled[key] as boolean;
    return !collapsibleDays || index < OPEN_DAYS_WHEN_COLLAPSED;
  };

  return (
    <div data-testid="task-timeline" className="flex flex-col">
      {groups.map((group, index) => {
        const open = isOpen(group.key, index);
        return (
          <div key={group.key} data-testid="timeline-day" data-day-key={group.key}>
            <DayHeader
              group={group}
              collapsible={collapsibleDays}
              open={open}
              onToggle={() => setToggled((t) => ({ ...t, [group.key]: !open }))}
              theme={theme}
            />
            {open ? (
              <ol className="flex flex-col" style={{ paddingLeft: 2 }}>
                {group.events.map((event, j) => (
                  <TimelineEntry
                    key={event.id}
                    eventType={event.event_type}
                    occurredAt={event.occurred_at}
                    described={describeEvent(event, lookups)}
                    actor={
                      event.actor_membership_id
                        ? byMembership.get(event.actor_membership_id)
                        : undefined
                    }
                    nodeLabel={attributionLabel(
                      event,
                      focalNodeId,
                      showNodeAttribution,
                      nodeTitleById,
                    )}
                    compact={compact}
                    lastInDay={j === group.events.length - 1}
                    tz={tz}
                  />
                ))}
              </ol>
            ) : (
              <PelletStrip group={group} theme={theme} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The sub-task title to attribute a descendant event to, or null when the event
 * is the focal node's own (or we're not in subtree scope). An unknown id (an
 * archived / removed node not in the title map) degrades to "a sub-task" — calm,
 * never a crash.
 */
function attributionLabel(
  event: NodeHistoryItem,
  focalNodeId: string,
  showNodeAttribution: boolean,
  nodeTitleById: Map<string, string> | undefined,
): string | null {
  if (!showNodeAttribution) return null;
  if (!event.node_id || event.node_id === focalNodeId) return null;
  return nodeTitleById?.get(event.node_id) ?? "a sub-task";
}

// ── Day header ────────────────────────────────────────────────────────────────

function DayHeader({
  group,
  collapsible,
  open,
  onToggle,
  theme,
}: {
  group: DayGroup<NodeHistoryItem>;
  collapsible: boolean;
  open: boolean;
  onToggle: () => void;
  theme: MapTheme;
}) {
  const editorial = theme.mode === "editorial";
  const c = theme.palette;
  const count = group.events.length;
  const countLabel = `${count} event${count === 1 ? "" : "s"}`;
  // EDITORIAL (`.t-history .day`): a mono UPPERCASE label, the faint date as a
  // `.ds` sub-label, and a mono uppercase "N events" count. LEGACY keeps the
  // shipped bold relative label + faint date, byte-identical.
  const inner = (
    <>
      {collapsible && (
        <span
          aria-hidden
          className="text-[10px] transition-transform"
          style={{
            color: editorial ? c.muted : PALETTE.muted,
            transform: open ? "rotate(90deg)" : "none",
          }}
        >
          ▶
        </span>
      )}
      <span
        className={editorial ? "text-[11px] font-semibold uppercase" : "text-[13px] font-bold"}
        style={
          editorial
            ? { color: c.ink, fontFamily: theme.fonts.mono, letterSpacing: "0.08em" }
            : { color: PALETTE.ink }
        }
      >
        {group.label}
      </span>
      {group.dateLabel && (
        <span
          className={editorial ? "text-[11px]" : "font-mono text-[11px]"}
          style={
            editorial
              ? { color: c.muted, fontFamily: theme.fonts.mono, letterSpacing: "0.04em" }
              : { color: PALETTE.muted }
          }
        >
          {group.dateLabel}
        </span>
      )}
      <span
        className={editorial ? "ml-auto text-[10.5px] uppercase" : "ml-auto text-[11px]"}
        style={
          editorial
            ? { color: c.muted, fontFamily: theme.fonts.mono, letterSpacing: "0.08em" }
            : { color: PALETTE.faint }
        }
      >
        {countLabel}
      </span>
    </>
  );

  if (collapsible) {
    return (
      <button
        type="button"
        data-testid="timeline-day-header"
        aria-expanded={open}
        onClick={onToggle}
        className="flex w-full items-baseline gap-2 pt-3.5 pb-2 text-left"
      >
        {inner}
      </button>
    );
  }
  return (
    <div data-testid="timeline-day-header" className="flex items-baseline gap-2 pt-3.5 pb-2">
      {inner}
    </div>
  );
}

// ── Collapsed-day pellet strip (A3) ─────────────────────────────────────────────

/**
 * A folded day's pellet strip: one tiny coloured dot per event, so you can see
 * WHAT kind of day it was (a state change, a few notes) before expanding it.
 * A count of the work's events, never a tally about a person (§A9).
 */
function PelletStrip({
  group,
  theme,
}: {
  group: DayGroup<NodeHistoryItem>;
  theme: MapTheme;
}) {
  return (
    <div data-testid="timeline-day-pellets" className="flex flex-wrap items-center gap-1 pb-2 pl-5">
      {group.events.map((event) => {
        const toState = stateOf(event);
        const visual = eventVisual(event.event_type, toState, theme);
        return (
          <span
            key={event.id}
            className="flex items-center justify-center rounded-full"
            style={{
              width: 16,
              height: 16,
              backgroundColor: visual.bg,
              color: visual.fg,
              border: `1px solid ${visual.fg}66`,
            }}
          >
            <EventIcon type={event.event_type} size={9} />
          </span>
        );
      })}
    </div>
  );
}

/** The destination state of a `state_changed` event, for pellet colour. */
function stateOf(event: NodeHistoryItem): "planned" | "in_progress" | "blocked" | "done" | null {
  if (event.event_type !== "state_changed") return null;
  const to = (event.payload as Record<string, unknown> | null | undefined)?.to;
  return to === "planned" || to === "in_progress" || to === "blocked" || to === "done" ? to : null;
}
